import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
} from 'react-router';
import {
  ChevronDown,
  Clock,
  Globe,
  Link2,
  Pencil,
  Plus,
  Shield,
  Shuffle,
  Smartphone,
  Target,
  X,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ApiError,
  analytics as analyticsApi,
  domains as domainsApi,
  links as linksApi,
  pages as pagesApi,
  type AnalyticsRange,
  type AnalyticsSummary,
  type Domain,
  type LinkAnalytics,
  type LinkDetail as LinkRecord,
  type LinkShieldAnalytics,
  type BlockCtrRow,
  type PageCtrAnalytics,
  type VariantCtrBreakdown,
  type PageDetail,
  type PageSummary,
  type ShieldCategory,
  type ShieldConfig,
  type Variation,
  type VariationConditions,
  type VariationAction,
} from '@/lib/api';
import { countryFlag, countryName } from '@/lib/countries';
// renderPage + normalizeTheme: power the landing-page heatmap preview.
// Same renderer the bouncer uses for production traffic, so the
// dashboard heatmap shows the literal HTML visitors saw — no
// approximation, no parallel implementation drift.
import { normalizeTheme, renderPage, type Block } from '@poof/templates';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LinkPixelOverride } from '@/components/LinkPixelOverride';
import { QRCode } from '@/components/QRCode';
import { CountryPicker } from '@/components/CountryPicker';
import { InlineEditableTitle } from '@/components/InlineEditableTitle';
import { LiveNowCard } from '@/components/LiveNowCard';
import { MetricCard } from '@/components/MetricCard';
import { TodayVsYesterdayCard } from '@/components/TodayVsYesterdayCard';

const FREE_HOST = 'go.poof.bio';

const LINK_RANGES: Array<{ label: string; value: AnalyticsRange }> = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
];

/** Label for the "Visits" stat card — adapts to the active range so the
 *  customer always knows what window the headline number covers. */
const LINK_RANGE_LABEL: Record<AnalyticsRange, string> = {
  today: 'Visits today',
  yesterday: 'Visits yesterday',
  '7d': 'Visits (7d)',
  '30d': 'Visits (30d)',
  '90d': 'Visits (90d)',
};

export default function LinkDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [link, setLink] = useState<LinkRecord | null>(null);
  const [stats, setStats] = useState<LinkAnalytics | null>(null);
  // Time range for the analytics section. Mirrors the org /app/analytics
  // page: Today / Yesterday (hourly) or 7/30/90 days (daily).
  const [range, setRange] = useState<AnalyticsRange>('7d');
  // Secondary fetch from the filtered org analytics endpoint — same data
  // the /app/analytics?link=<id> view shows, kept in sync via shared 30s
  // KV cache so this is essentially free. Used by the heatmap row's
  // right-hand column (Top countries + By device) so we don't duplicate
  // those AE queries on the per-link endpoint.
  const [orgFiltered, setOrgFiltered] = useState<AnalyticsSummary | null>(null);
  const [shieldStats, setShieldStats] = useState<LinkShieldAnalytics | null>(null);
  const [domainsList, setDomainsList] = useState<Domain[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Edit form state — see "Behavior" section below for what these mean.
  // Note: the link's `title` is no longer in the form. It's edited inline
  // at the page header via <InlineEditableTitle>, which calls
  // linksApi.update directly and refetches.
  const [targetUrl, setTargetUrl] = useState('');
  const [domainId, setDomainId] = useState<string>('');
  const [action, setAction] = useState<'redirect' | 'page'>('redirect');
  const [escapeIab, setEscapeIab] = useState(true);
  const [pageId, setPageId] = useState<string>('');
  const [pagesList, setPagesList] = useState<PageSummary[]>([]);
  // Poof Shield
  const [shieldEnabled, setShieldEnabled] = useState(false);
  const [shield, setShield] = useState<ShieldConfig>(DEFAULT_SHIELD_UI);
  // Visitor variations — replaces both A/B test and routing rules.
  // See packages/shared/src/variations.ts for the picker semantics.
  const [variations, setVariations] = useState<Variation[]>([]);
  // Age-gate ("18+") interstitial. Independent of Poof Shield —
  // Shield filters bots, the gate filters age. They can be on together.
  const [ageGateEnabled, setAgeGateEnabled] = useState(false);
  const [ageGateMemory, setAgeGateMemory] = useState<'always' | 'session' | '24h'>('24h');
  const [ageGateOnDecoy, setAgeGateOnDecoy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [showQr, setShowQr] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Load link + stats + domain list in parallel.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    linksApi
      .get(id)
      .then((l) => {
        if (!alive) return;
        setLink(l);
        setTargetUrl(l.targetUrl);
        setDomainId(l.domainId || '');
        setAction(l.defaultAction === 'page' ? 'page' : 'redirect');
        setEscapeIab(l.escapeIab !== false);
        setPageId(l.pageId || '');
        setShieldEnabled(!!l.shieldEnabled);
        if (l.shieldConfig) setShield(l.shieldConfig);
        if (l.variations) setVariations(l.variations);
        setAgeGateEnabled(!!l.ageGateEnabled);
        setAgeGateMemory(l.ageGateMemory || '24h');
        setAgeGateOnDecoy(!!l.ageGateOnDecoy);
      })
      .catch((e) => alive && setLoadErr((e as Error).message || 'Failed to load link.'));
    analyticsApi
      .link(id, range)
      .then((s) => alive && setStats(s))
      .catch(() => {});
    analyticsApi
      .summary(range, false, { link: id })
      .then((s) => alive && setOrgFiltered(s))
      .catch(() => {});
    analyticsApi
      .shield(id, 7)
      .then((s) => alive && setShieldStats(s))
      .catch(() => {});
    domainsApi
      .list()
      .then((r) => alive && setDomainsList(r.domains.filter((d) => d.sslStatus === 'active')))
      .catch(() => {});
    pagesApi
      .list()
      .then((r) => alive && setPagesList(r.pages))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // range is in deps so flipping the time-range picker refetches analytics
    // (link summary + org-filtered country/device). eslint-disable: the form
    // fields below are intentionally NOT in deps — they're populated once on
    // initial link load and shouldn't reset on range changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, range]);

  // Auto-refresh stats every 30s — same cadence as the org dashboard.
  useEffect(() => {
    if (!id) return;
    const t = setInterval(() => {
      analyticsApi.link(id, range).then(setStats).catch(() => {});
      analyticsApi.summary(range, false, { link: id }).then(setOrgFiltered).catch(() => {});
      analyticsApi.shield(id, 7).then(setShieldStats).catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
  }, [id, range]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!link) return;
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      const updated = await linksApi.update(link.id, {
        targetUrl,
        domainId: domainId || null,
        defaultAction: action,
        escapeIab,
        pageId: action === 'page' ? pageId || null : null,
        shieldEnabled,
        shieldConfig: shield,
        variations,
        ageGateEnabled,
        ageGateMemory,
        ageGateOnDecoy,
      });
      setLink(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setErr(
        e instanceof ApiError ? errorMessageFor(e.code) : (e as Error).message,
      );
    } finally {
      setSaving(false);
    }
  }

  async function onConfirmDelete() {
    if (!link) return;
    await linksApi.delete(link.id);
    navigate('/app/links');
  }

  if (loadErr) {
    return (
      <DashboardLayout title="Link">
        <RouterLink to="/app/links" className="text-sm text-[var(--color-muted-foreground)] hover:underline">
          ← Back to links
        </RouterLink>
        <p className="mt-6 text-sm text-[var(--color-destructive)]">{loadErr}</p>
      </DashboardLayout>
    );
  }

  if (!link) {
    return (
      <DashboardLayout title="Loading link">
        <RouterLink to="/app/links" className="text-sm text-[var(--color-muted-foreground)] hover:underline">
          ← Back to links
        </RouterLink>
        <div className="mt-4 h-9 w-72 animate-pulse rounded-md bg-[var(--color-muted)]" />
        <div className="mt-3 h-4 w-96 animate-pulse rounded bg-[var(--color-muted)]" />
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="h-24 animate-pulse rounded-xl bg-[var(--color-muted)]" />
          <div className="h-24 animate-pulse rounded-xl bg-[var(--color-muted)]" />
          <div className="h-24 animate-pulse rounded-xl bg-[var(--color-muted)]" />
        </div>
        <div className="mt-6 h-48 animate-pulse rounded-xl bg-[var(--color-muted)]" />
      </DashboardLayout>
    );
  }

  const dirty =
    targetUrl !== link.targetUrl ||
    (domainId || null) !== (link.domainId || null) ||
    action !== (link.defaultAction === 'page' ? 'page' : 'redirect') ||
    escapeIab !== (link.escapeIab !== false) ||
    (action === 'page' && (pageId || '') !== (link.pageId || '')) ||
    shieldEnabled !== !!link.shieldEnabled ||
    ageGateEnabled !== !!link.ageGateEnabled ||
    ageGateMemory !== (link.ageGateMemory ?? '24h') ||
    ageGateOnDecoy !== !!link.ageGateOnDecoy ||
    JSON.stringify(shield) !== JSON.stringify(link.shieldConfig ?? DEFAULT_SHIELD_UI) ||
    JSON.stringify(variations) !== JSON.stringify(link.variations ?? []);

  const activeHostname =
    domainId === ''
      ? FREE_HOST
      : domainsList.find((d) => d.id === domainId)?.hostname || link.hostname;

  return (
    // No `title` prop — the editable inline title below IS the page heading.
    // Passing a title here would duplicate it (DashboardLayout would render
    // its own h1 + ours, both showing the same value).
    <DashboardLayout>
      <RouterLink to="/app/links" className="text-sm text-[var(--color-muted-foreground)] hover:underline">
        ← Back to links
      </RouterLink>

      {/* Header — inline-editable title, click the pencil to rename. */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="min-w-0">
            <InlineEditableTitle
              value={link.title}
              fallback={link.shortCode}
              iconSize={20}
              textClassName="font-display text-3xl font-bold tracking-tight md:text-4xl"
              onSave={async (next) => {
                await linksApi.update(link.id, { title: next });
                const fresh = await linksApi.get(link.id);
                setLink(fresh);
              }}
            />
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <a
              href={link.fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-sm text-[var(--color-primary)] hover:underline"
            >
              {link.fullUrl} ↗
            </a>
            <CopyButton text={link.fullUrl} />
            <button
              onClick={() => setShowQr(true)}
              className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium transition hover:bg-[var(--color-muted)]"
            >
              QR code
            </button>
          </div>
          {/* Show what visitors actually see when they click this link.
              Was always showing the targetUrl, which is misleading on
              page links — the visitor lands on the rendered page, not
              the underlying URL. Now:
                - page action → "Landing page: <pageName>"
                - redirect/escape → the actual target URL */}
          <div className="mt-1.5 text-xs text-[var(--color-muted-foreground)]">
            {link.defaultAction === 'page' && link.pageId ? (
              <span>
                Serves landing page
                {stats?.pageCtr?.pageName ? (
                  <>
                    {': '}
                    <span className="font-medium text-[var(--color-foreground)]">
                      {stats.pageCtr.pageName}
                    </span>
                  </>
                ) : null}
              </span>
            ) : (
              <span>→ {link.targetUrl}</span>
            )}
          </div>
        </div>
      </div>

      {/* Range picker — sits between the link metadata and the stats
          grid. The Live Now chip that used to live here moved into its
          own first-class stat card below. */}
      <div className="mt-8 flex justify-end">
        <div className="flex gap-1 rounded-full border border-[var(--color-border)] bg-[var(--surface-deep)] p-1 text-xs">
          {LINK_RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`rounded-full px-3 py-1 transition ${
                r.value === range
                  ? 'bg-[var(--color-primary)] font-semibold text-[var(--color-primary-foreground)]'
                  : 'text-[var(--color-on-surface-variant)] hover:text-[var(--color-foreground)]'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* 4-card stats grid — same MetricCard primitive everywhere so
          the bottom rows align. Visits anchors the left, Live Now sits
          on the right (matches the org /app/analytics layout). */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label={LINK_RANGE_LABEL[range]}
          value={(stats?.totalClicks ?? 0).toLocaleString()}
          meta={stats ? 'Page loads + redirects' : 'Loading…'}
        />
        {stats ? (
          <TodayVsYesterdayCard delta={stats.delta} />
        ) : (
          <MetricCard label="Today vs yesterday" value="—" meta="Loading…" />
        )}
        <MetricCard
          label="Top country"
          value={stats?.byCountry[0]?.country.toUpperCase() || '—'}
          meta={
            stats?.byCountry[0]
              ? `${stats.byCountry[0].clicks.toLocaleString()} clicks`
              : 'No data yet'
          }
        />
        {stats ? (
          <LiveNowCard liveNow={stats.liveNow} />
        ) : (
          <MetricCard label="Live now" value="—" meta="Loading…" />
        )}
      </div>

      {/* Time series */}
      {stats && stats.byDay.some((d) => d.clicks > 0) && (
        <div className="mt-6 rounded-2xl glass p-5">
          <div className="mb-3 text-sm font-medium">Clicks over time</div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <AreaChart
                data={stats.byDay.map((d) => ({
                  // Hour buckets are "YYYY-MM-DD HH:00" → "HH:00".
                  // Day buckets are "YYYY-MM-DD" → "MM-DD".
                  day:
                    stats.granularity === 'hour'
                      ? d.day.slice(11, 16)
                      : d.day.slice(5),
                  clicks: d.clicks,
                }))}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient id="linkClicksGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#cdbdff" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#cdbdff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="day" stroke="var(--color-muted-foreground)" fontSize={12} />
                <YAxis stroke="var(--color-muted-foreground)" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(20, 20, 20, 0.85)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 12,
                    fontSize: 12,
                    boxShadow: '0 12px 32px -16px rgba(0,0,0,0.6)',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="clicks"
                  stroke="#cdbdff"
                  strokeWidth={2}
                  fill="url(#linkClicksGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Landing page CTR — only for 'page' action links */}
      {link.defaultAction === 'page' && link.pageId && (
        <PageCtrSection ctr={stats?.pageCtr} orgFiltered={orgFiltered} />
      )}

      {/* Poof Shield activity */}
      <ShieldActivity
        shieldStats={shieldStats}
        shieldEnabled={!!link.shieldEnabled}
        totalClicks={stats?.totalClicks ?? 0}
      />

      {/* Settings — collapsible cards.
       *
       * Analytics block above is what customers look at most; below it,
       * every section that USED to stack as a 200-line scroll is now a
       * one-line summary chip the customer can expand on demand. Open
       * state persists in localStorage per-section. All four cards are
       * closed on first visit so the page lands calm.
       */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold">Settings</h2>
        <form onSubmit={onSave} className="mt-4 flex flex-col gap-3">
          <CollapsibleCard
            id="destination"
            title="Destination & Behavior"
            summary={summarizeDestination({
              action,
              targetUrl,
              pageId,
              pagesList,
              escapeIab,
              ageGateEnabled,
            })}
          >
            <BehaviorPanel
              action={action}
              setAction={setAction}
              escapeIab={escapeIab}
              setEscapeIab={setEscapeIab}
              pageId={pageId}
              setPageId={setPageId}
              pagesList={pagesList}
              currentPageId={link.pageId || null}
              targetUrl={targetUrl}
              setTargetUrl={setTargetUrl}
              ageGateEnabled={ageGateEnabled}
              setAgeGateEnabled={setAgeGateEnabled}
              ageGateMemory={ageGateMemory}
              setAgeGateMemory={setAgeGateMemory}
              ageGateOnDecoy={ageGateOnDecoy}
              setAgeGateOnDecoy={setAgeGateOnDecoy}
            />

            {domainsList.length > 0 && (
              <label className="mt-4 flex flex-col gap-1.5">
                <span className="text-sm font-medium">Domain</span>
                <select
                  value={domainId}
                  onChange={(e) => setDomainId(e.target.value)}
                  className="rounded-md border border-[var(--color-border)] bg-transparent px-3 py-2 outline-none transition focus:border-[var(--color-ring)]"
                >
                  <option value="">{FREE_HOST} (free subdomain)</option>
                  {domainsList.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.hostname}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  Changing domain rewrites the public URL to{' '}
                  <code className="font-mono">https://{activeHostname}/{link.shortCode}</code>.
                  The old URL will 404.
                </span>
              </label>
            )}

            {/* Short code — read-only, kept here because it's identity
                metadata for the destination. Wrapped in opacity-60 to
                visually deprioritise. */}
            <div className="mt-4 flex flex-col gap-1.5 opacity-60">
              <span className="text-sm font-medium">Short code</span>
              <input
                type="text"
                value={link.shortCode}
                readOnly
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-muted)] px-3 py-2 font-mono text-sm outline-none"
              />
              <span className="text-xs text-[var(--color-muted-foreground)]">
                Locked — changing it would break any URLs you&apos;ve already shared.
                Make a new link instead.
              </span>
            </div>
          </CollapsibleCard>

          <CollapsibleCard
            id="shield"
            title="Poof Shield"
            summary={summarizeShield({ shieldEnabled, shield, pagesList })}
          >
            <ShieldPanel
              enabled={shieldEnabled}
              setEnabled={setShieldEnabled}
              config={shield}
              setConfig={setShield}
              pagesList={pagesList}
            />
          </CollapsibleCard>

          <CollapsibleCard
            id="variations"
            title="A/B Testing & Rules"
            badge={
              <span className="rounded-full bg-[var(--color-primary)]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-primary)]">
                Pro · free during beta
              </span>
            }
            summary={summarizeVariations(variations)}
          >
            <VariationsSection
              variations={variations}
              setVariations={setVariations}
              pagesList={pagesList}
              shieldEnabled={shieldEnabled}
            />
          </CollapsibleCard>

          {err && <p className="text-sm text-[var(--color-destructive)]">{err}</p>}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={!dirty || saving}
              className="rounded-full bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-[var(--color-primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {saved && <span className="text-sm text-[#16a34a]">✓ Saved</span>}
          </div>
        </form>

        {/* Meta Pixel (advanced) — sits outside the main form because it
            manages its own save chain against /api/links/[id]/pixel.
            Saving here doesn't touch the rest of the link settings. */}
        <div className="mt-6">
          <LinkPixelOverride linkId={link.id} />
        </div>

        <div className="mt-6">
          <CollapsibleCard
            id="danger"
            title="Danger zone"
            summary="Delete this link permanently."
          >
            <div className="space-y-3">
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Deleting this link is immediate — the URL{' '}
                <code className="font-mono">{link.fullUrl}</code> will 404 for anyone who taps
                it. Historical analytics stay in your Analytics tab.
              </p>
              <button
                type="button"
                onClick={() => setShowDelete(true)}
                className="rounded-full border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-4 py-1.5 text-sm font-medium text-[var(--color-destructive)] transition hover:bg-[var(--color-destructive)]/20"
              >
                Delete this link
              </button>
            </div>
          </CollapsibleCard>
        </div>
      </div>

      {/* QR modal */}
      {showQr && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => setShowQr(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative rounded-2xl bg-white p-8 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <QRCode value={link.fullUrl} size={260} />
            <p className="mt-4 font-mono text-xs text-[#0a0a0a]">{link.fullUrl}</p>
            <button
              onClick={() => setShowQr(false)}
              className="mt-4 text-xs text-neutral-600 underline"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={onConfirmDelete}
        title={`Delete ${link.title || link.shortCode}?`}
        description={
          <>
            <p>
              The URL{' '}
              <code className="font-mono">{link.fullUrl}</code> will 404 immediately.
              Followers who tap it from your bio will land on an error page until you
              create a replacement.
            </p>
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              Historical analytics for this link will remain in your Analytics tab
              (clicks already recorded don&apos;t disappear).
            </p>
          </>
        }
        requireText={link.shortCode}
        confirmLabel="Delete link"
        destructive
      />
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Landing page CTR — visits, total clicks, per-block click-through rate.
// Only rendered when this link's action is 'page' (redirect links don't
// have a CTR concept — visits == clicks). Drives "Variant A page got 25%
// CTR, Variant B got 35%" comparisons.
// ---------------------------------------------------------------------------

function PageCtrSection({
  ctr,
  orgFiltered,
}: {
  ctr: PageCtrAnalytics | undefined;
  /** /api/analytics?link=<this link's id> — same data the analytics page
   *  shows when filtered to this link. Drives the right-side Top
   *  countries + By device column. */
  orgFiltered: AnalyticsSummary | null;
}) {
  if (!ctr) {
    return (
      <div className="mt-8 rounded-xl border border-dashed border-[var(--color-border)] p-5">
        <div className="flex items-start gap-3">
          <div className="text-2xl">📊</div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold">Landing page click-through</h2>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              As real visitors land on this page and tap your buttons, you&apos;ll see
              their click-through rate here — overall and per block.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  // Country + device breakdown. Same JSX is used in both the A/B-variant
  // branch and the per-block branch, so define it once. The data comes
  // from /api/analytics?link=<id> (the org-level filtered endpoint), so
  // it covers all variants combined for the A/B case — exactly the
  // link-wide view that's useful regardless of which variant a visitor
  // got served.
  const countryDeviceColumn = (
    // lg:w-64 (was w-56) — gives country names like "United Kingdom" /
    // "Saudi Arabia" room to render without cramping into the count.
    // Middle per-block column is flex-1 so it absorbs the difference.
    <div className="space-y-5 lg:w-64 lg:shrink-0">
      <BreakdownMini
        title="Top countries"
        rows={(orgFiltered?.byCountry ?? []).slice(0, 8).map((c) => ({
          key: c.country,
          icon: countryFlag(c.country),
          label: countryName(c.country),
          value: c.clicks,
        }))}
        emptyHint="No country data yet"
      />
      <BreakdownMini
        title="By device"
        rows={(orgFiltered?.byDevice ?? []).slice(0, 4).map((d) => ({
          key: d.device,
          icon: deviceIcon(d.device),
          label: prettyDevice(d.device),
          value: d.clicks,
        }))}
        emptyHint="No device data yet"
      />
    </div>
  );
  return (
    <div className="mt-8 rounded-2xl glass p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          📊 Landing page click-through
        </h2>
        <span className="text-xs text-[var(--color-muted-foreground)]">
          on &ldquo;{ctr.pageName}&rdquo; · last 7 days
        </span>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-[var(--color-muted)]/40 p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Page views
          </div>
          <div className="mt-1.5 text-3xl font-semibold tabular-nums">
            {ctr.views.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            total renders (refreshes count)
          </div>
        </div>
        <div className="rounded-lg bg-[var(--color-muted)]/40 p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Unique visitors
          </div>
          <div className="mt-1.5 text-3xl font-semibold tabular-nums">
            {ctr.uniqueVisitors.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            individual people
          </div>
        </div>
        <div className="rounded-lg bg-[var(--color-muted)]/40 p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Clicks
          </div>
          <div className="mt-1.5 text-3xl font-semibold tabular-nums">
            {ctr.clicks.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            total taps (refreshes & repeats count)
          </div>
        </div>
        <div className="rounded-lg bg-[var(--color-muted)]/40 p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
            CTR
          </div>
          <div className="mt-1.5 text-3xl font-semibold tabular-nums">
            {ctr.uniqueVisitors > 0 ? pct(ctr.overallCtr) : '—'}
          </div>
          <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            clicks ÷ unique visitors
          </div>
        </div>
      </div>

      {ctr.perVariant && ctr.perVariant.length > 0 ? (
        // A/B testing on: variant cards (middle, flex-1) + the country/
        // device column (right). The lander heatmap is omitted in A/B mode
        // because variants can serve different pages — one preview would
        // be misleading, N previews would explode the page.
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            <VariantComparison variants={ctr.perVariant} />
          </div>
          {countryDeviceColumn}
        </div>
      ) : ctr.perBlock.length > 0 ? (
        // Three columns at lg+: phone preview (left), per-block taps
        // (middle), Top countries + By device (right). Drops to two
        // columns at md (no right column), one column on mobile.
        <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-start">
          <div className="md:shrink-0">
            <LanderHeatmap pageId={ctr.pageId} perBlock={ctr.perBlock} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              Per-block taps
            </div>
            <PerBlockList rows={ctr.perBlock} />
          </div>
          {countryDeviceColumn}
        </div>
      ) : (
        <p className="mt-5 text-sm text-[var(--color-muted-foreground)]">
          No outbound clicks recorded yet. Once visitors start tapping buttons on your
          page, each block&apos;s CTR will appear here.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LanderHeatmap — visual click breakdown.
//
// Renders the customer's actual landing page in an iframe (using the SAME
// `renderPage` function the bouncer uses, so what we show is byte-for-byte
// what visitors saw), with a small floating badge on each clickable block
// showing "X clicks · Y%". Mobile-frame width because that's where 95% of
// bio-link traffic lives.
//
// The iframe is sandboxed but allows scripts — we need a tiny inline script
// to walk the DOM after render and inject the badges. The script has no
// network/storage/cookie access (default sandbox).
// ---------------------------------------------------------------------------

function LanderHeatmap({
  pageId,
  perBlock,
}: {
  pageId: string;
  perBlock: BlockCtrRow[];
}) {
  const [page, setPage] = useState<PageDetail | null>(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    pagesApi
      .get(pageId)
      .then((p) => {
        if (!cancelled) setPage(p);
      })
      .catch(() => {
        if (!cancelled) setLoadErr(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pageId]);

  const html = useMemo(() => {
    if (!page) return '';
    try {
      const base = renderPage(
        {
          blocks: page.blocks as Block[],
          theme: normalizeTheme(page.theme),
          meta: page.meta,
          name: page.name,
        },
        { showBranding: false, plan: 'free', tracking: false },
      );

      // Per-block click data the inline script reads. JSON.stringify is
      // safe to inline because every key is a known UUID and every value
      // is a number — no user-controlled strings.
      const clickData: Record<string, { clicks: number; ctr: number }> = {};
      for (const b of perBlock) {
        // Skip rows for blocks that no longer exist in the page — the
        // badge wouldn't render anyway because the wrapper isn't in
        // the DOM.
        if (b.blockType === '__removed__') continue;
        clickData[b.blockId] = { clicks: b.clicks, ctr: b.ctr };
      }

      // Inject heatmap CSS + script before </body>. The CSS overrides
      // the renderer's `display: contents` on block wrappers so we can
      // absolute-position a badge inside each one. We also hide the
      // iframe's own scrollbars — the iframe renders at the iPhone
      // Pro Max viewport (430×932) and the host wrapper scales it down,
      // so a real-world content height fits without scroll.
      const inject = `
<style>
  html, body {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  html::-webkit-scrollbar,
  body::-webkit-scrollbar { display: none; }

  [data-pb-block-id] {
    display: block !important;
    position: relative !important;
  }
  .pb-hm-badge {
    position: absolute;
    /* Slightly larger top/right to balance the wider chip. */
    top: 8px; right: 8px;
    background: rgba(79, 70, 229, 0.95);
    color: #fff;
    /* Bumped from 11px/4-8 to 17px/6-12 to counter the 0.65 host scale
       so the badge still reads ~11px to the eye in the dashboard. */
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 17px;
    font-weight: 600;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif;
    pointer-events: none;
    z-index: 100;
    box-shadow: 0 3px 12px rgba(0,0,0,0.25);
    letter-spacing: 0.2px;
    white-space: nowrap;
    line-height: 1.2;
  }
  .pb-hm-badge.zero {
    background: rgba(120, 113, 108, 0.85);
  }
  /* Soften non-clickable blocks so the eye lands on the badged ones. */
  [data-pb-block-id]:not(.pb-hm-clickable) {
    opacity: 0.55;
  }
</style>
<script>
(function(){
  var data = ${JSON.stringify(clickData)};
  var wraps = document.querySelectorAll('[data-pb-block-id]');
  for (var i = 0; i < wraps.length; i++) {
    var w = wraps[i];
    var id = w.getAttribute('data-pb-block-id');
    var anchors = w.querySelectorAll('a[href]');
    if (anchors.length === 0) continue;
    w.classList.add('pb-hm-clickable');
    var d = data[id] || { clicks: 0, ctr: 0 };
    var pct = (d.ctr * 100).toFixed(1) + '%';
    var label = d.clicks + (d.clicks === 1 ? ' tap · ' : ' taps · ') + pct;
    var badge = document.createElement('div');
    badge.className = 'pb-hm-badge' + (d.clicks === 0 ? ' zero' : '');
    badge.textContent = label;
    w.appendChild(badge);
  }
})();
</script>`;

      return base.includes('</body>')
        ? base.replace('</body>', inject + '</body>')
        : base + inject;
    } catch {
      return '';
    }
  }, [page, perBlock]);

  if (loadErr) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] p-6 text-sm text-[var(--color-muted-foreground)]">
        Couldn&apos;t load the landing page preview. The page may have been
        deleted.
      </div>
    );
  }

  if (!page) {
    return (
      <div>
        <div className="h-[605px] w-[280px] animate-pulse rounded-2xl border border-[var(--color-border)] bg-[var(--color-muted)]/30" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <div className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          Click heatmap
        </div>
        <div className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">
          Badges show taps · CTR
        </div>
      </div>
      {/*
        The iframe renders at iPhone Pro Max viewport (430×932) so the
        landing page's own layout has room to breathe — same as what
        visitors see on a real phone. The host wrapper is sized at the
        scaled-down dimensions (280×605) with overflow:hidden, and the
        iframe is CSS-transform-scaled to fit. Result: no scrollbar,
        no cramped content, looks like an actual phone screenshot.
      */}
      <div
        className="overflow-hidden rounded-2xl border border-[var(--color-border)] shadow-sm"
        style={{ width: 280, height: 605 }}
      >
        <iframe
          title="Landing page click heatmap"
          srcDoc={html}
          sandbox="allow-scripts"
          style={{
            width: 430,
            height: 932,
            transform: 'scale(0.651)',
            transformOrigin: 'top left',
            border: 'none',
            display: 'block',
            background: '#fff',
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-block list — reusable both at the page level and inside each variant
// card. Renders the same bar-graph layout for any list of BlockCtrRow.
// ---------------------------------------------------------------------------

function PerBlockList({ rows }: { rows: BlockCtrRow[] }) {
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  return (
    <ul className="mt-3 space-y-2">
      {rows.map((row) => {
        const widthPct = Math.min(100, row.ctr * 100);
        return (
          <li
            key={row.blockId}
            className="rounded-lg border border-[var(--color-border)] p-3"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0 flex-1 truncate">
                <span className="mr-1.5 text-xs text-[var(--color-muted-foreground)]">
                  {iconForBlockType(row.blockType)}
                </span>
                <span className="font-medium">{row.label || row.blockType || 'Block'}</span>
              </div>
              <div className="flex items-baseline gap-3 text-sm tabular-nums">
                <span className="text-[var(--color-muted-foreground)]">
                  {row.clicks.toLocaleString()}
                  <span className="ml-1 text-[10px] opacity-70">
                    {row.clicks === 1 ? 'tap' : 'taps'}
                  </span>
                </span>
                <span className="font-mono font-medium" title="clicks ÷ unique visitors">
                  {pct(row.ctr)}
                </span>
              </div>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--color-muted)]">
              <div
                className="h-full bg-[var(--color-primary)]"
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// BreakdownMini — compact list used in the heatmap row's right column for
// Top countries + By device. Rows render as `icon  label .................. count`
// with truncation. Empty state shows a short hint.
// ---------------------------------------------------------------------------

interface BreakdownMiniRow {
  key: string;
  icon: string;
  label: string;
  value: number;
}

function BreakdownMini({
  title,
  rows,
  emptyHint,
}: {
  title: string;
  rows: BreakdownMiniRow[];
  emptyHint: string;
}) {
  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {title}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-[var(--color-muted-foreground)]">{emptyHint}</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center gap-2 text-sm">
              <span aria-hidden className="shrink-0 text-base leading-none">
                {r.icon}
              </span>
              <span className="min-w-0 flex-1 truncate" title={r.label}>
                {r.label}
              </span>
              <span className="shrink-0 font-mono text-xs tabular-nums text-[var(--color-muted-foreground)]">
                {r.value.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function prettyDevice(s: string): string {
  switch (s) {
    case 'mobile':
      return 'Phone';
    case 'tablet':
      return 'Tablet';
    case 'desktop':
      return 'Desktop';
    default:
      return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

function deviceIcon(s: string): string {
  switch (s) {
    case 'mobile':
      return '📱';
    case 'tablet':
      return '📲';
    case 'desktop':
      return '💻';
    default:
      return '·';
  }
}

// ---------------------------------------------------------------------------
// Variant comparison — side-by-side cards when an A/B test is active. The
// highest overall-CTR variant gets a 🏆 winner chip (only when at least
// one variant has views; "winner" with 0 traffic is misleading).
// ---------------------------------------------------------------------------

function VariantComparison({ variants }: { variants: VariantCtrBreakdown[] }) {
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  // Pick winner only when meaningful — at least one variant must have
  // recorded unique visitors, and we need a clear leader (ties show no
  // chip). Use uniqueVisitors as the gate so a "winner" with 1 view +
  // 1 click (100% CTR) doesn't drown out a real signal.
  const withVisitors = variants.filter((v) => v.uniqueVisitors > 0);
  let winnerId: string | null = null;
  if (withVisitors.length >= 2) {
    const sorted = [...withVisitors].sort((a, b) => b.overallCtr - a.overallCtr);
    if (sorted[0]!.overallCtr > (sorted[1]?.overallCtr ?? 0)) {
      winnerId = sorted[0]!.variantId;
    }
  }
  return (
    // No mt-6 here — the wrapping flex in PageCtrSection handles spacing
    // so this can sit beside the country/device column without double margin.
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
          A/B variants ({variants.length})
        </div>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Each variant&apos;s own visitors and clicks
        </p>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {variants.map((v) => {
          const isWinner = v.variantId === winnerId;
          return (
            <div
              key={v.variantId}
              className={
                'rounded-xl border p-4 ' +
                (isWinner
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                  : 'border-[var(--color-border)]')
              }
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-semibold">{v.variantLabel}</h3>
                    {isWinner ? (
                      <span className="rounded-full bg-[var(--color-primary)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                        🏆 Winner
                      </span>
                    ) : null}
                  </div>
                  {v.pageName ? (
                    <p className="mt-0.5 truncate text-xs text-[var(--color-muted-foreground)]">
                      → {v.pageName}
                    </p>
                  ) : null}
                </div>
                <div className="text-right">
                  <div className="font-mono text-2xl font-semibold tabular-nums">
                    {v.uniqueVisitors > 0 ? pct(v.overallCtr) : '—'}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
                    CTR
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-md bg-[var(--color-muted)]/40 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
                    Unique visitors
                  </div>
                  <div className="mt-0.5 text-base font-semibold tabular-nums">
                    {v.uniqueVisitors.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-[var(--color-muted-foreground)]">
                    {v.views.toLocaleString()} views
                  </div>
                </div>
                <div className="rounded-md bg-[var(--color-muted)]/40 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
                    Clicks
                  </div>
                  <div className="mt-0.5 text-base font-semibold tabular-nums">
                    {v.clicks.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-[var(--color-muted-foreground)]">
                    total taps
                  </div>
                </div>
              </div>
              {v.perBlock.length > 0 ? (
                <details className="mt-3 group">
                  <summary className="cursor-pointer text-xs font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                    <span className="group-open:hidden">Show per-block CTR ↓</span>
                    <span className="hidden group-open:inline">Hide per-block CTR ↑</span>
                  </summary>
                  <PerBlockList rows={v.perBlock} />
                </details>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function iconForBlockType(type: string): string {
  switch (type) {
    case 'link-button':
      return '🔘';
    case 'link-list':
      return '📋';
    case 'social-row':
      return '🔗';
    case 'sticky-cta':
      return '📌';
    case 'carousel':
      return '🖼️';
    case 'video-embed':
      return '🎬';
    case 'instagram-bio':
      return '📸';
    default:
      return '·';
  }
}

// ---------------------------------------------------------------------------
// Applicable routing rules — read-only summary on the link page so the
// customer can see what rules will fire here. Edits happen at /app/rules.
// ---------------------------------------------------------------------------

// (ApplicableRules + summarizeRule removed 2026-05-18 — replaced by
// VariationsSection inside the link editor. Each link now manages its
// own variations in place; the cross-link overview lives at
// /app/variations.)

// ---------------------------------------------------------------------------
// Poof Shield activity — aggregate counts ONLY. No UA strings, no ASNs,
// no per-bot detail. Friendly labels grouped from internal classifications.
// ---------------------------------------------------------------------------

// Customer-facing label + icon for each shield category. `hint`
// descriptions were removed because they spelled out what each
// category catches — useful for an internal admin page, but a free
// recipe for any competitor screenshotting the dashboard. The label
// alone says enough ("Meta scrapers" + "Bots & previewers" +
// "Country filtered" — visitors can guess from the names without
// us listing the UAs, ASN ranges, or protocols we match against).
const CATEGORY_LABELS: Record<ShieldCategory, { label: string; icon: string }> = {
  meta_scrapers: {
    label: 'Meta scrapers',
    icon: '🚫',
  },
  bots_and_previewers: {
    label: 'Bots & previewers',
    icon: '🤖',
  },
  country_filtered: {
    label: 'Country filtered',
    icon: '🌍',
  },
};

function ShieldActivity({
  shieldStats,
  shieldEnabled,
  totalClicks,
}: {
  shieldStats: LinkShieldAnalytics | null;
  shieldEnabled: boolean;
  totalClicks: number;
}) {
  // CTA when shield is off — small and out of the way, not a hard sell.
  if (!shieldEnabled) {
    return (
      <div className="mt-8 rounded-xl border border-dashed border-[var(--color-border)] p-5">
        <div className="flex items-start gap-3">
          <Shield size={22} className="shrink-0 text-[var(--color-primary)]" />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold">Poof Shield is off for this link</h2>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              Turn it on below to filter Meta scrapers, bot traffic, and unwanted regions
              away from your real fans &mdash; and see how much we caught here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading skeleton.
  if (!shieldStats) {
    return (
      <div className="mt-8 rounded-2xl glass p-6">
        <div className="h-5 w-48 animate-pulse rounded bg-[var(--color-muted)]" />
        <div className="mt-4 h-24 animate-pulse rounded bg-[var(--color-muted)]" />
      </div>
    );
  }

  // Denominator for the % — prefer "all traffic" reported by AE; if that
  // somehow comes back zero but the byCategory has data, fall back to the
  // existing link analytics totalClicks (which also counts decoys).
  const denominator = shieldStats.totalAll > 0 ? shieldStats.totalAll : totalClicks + shieldStats.totalBlocked;
  const pct = denominator > 0 ? Math.min(100, Math.round((shieldStats.totalBlocked / denominator) * 100)) : 0;

  // Sort and show only categories with non-zero counts.
  const rows = shieldStats.byCategory.filter((r) => r.count > 0);

  return (
    <div className="mt-8 rounded-2xl glass p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Shield size={18} className="text-[var(--color-primary)]" />
          Poof Shield activity
          <span className="rounded-full bg-[#16a34a]/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#16a34a]">
            ON
          </span>
        </h2>
        <span className="text-xs text-[var(--color-muted-foreground)]">Last 7 days</span>
      </div>

      {/* Top stats — kept to two cards only.
          Removed:
            • "Out of <denominator> total" subtext on Visitors Filtered
              — leaks our exact denominator (= total inbound traffic
              including bots), which is internal accounting, not a
              customer-facing fact.
            • The "Pixel saved" card entirely — it duplicates Visitors
              Filtered (same number, slightly different framing) and
              hints at our CAPI infrastructure. Out it goes. */}
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg bg-[var(--color-muted)]/40 p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Visitors filtered
          </div>
          <div className="mt-1.5 text-3xl font-semibold">
            {shieldStats.totalBlocked.toLocaleString()}
          </div>
        </div>
        <div className="rounded-lg bg-[var(--color-muted)]/40 p-4">
          <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
            % of traffic
          </div>
          <div className="mt-1.5 text-3xl font-semibold">{pct}%</div>
          <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            never saw your destination
          </div>
        </div>
      </div>

      {/* Per-category breakdown. Header + per-row hints removed: both
          quietly leaked our detection recipe (e.g. "Instagram &
          Facebook's own crawlers and previewers, including traffic
          from inside Meta's network" tells a competitor exactly which
          UAs + ASN ranges to copy). Customer-facing label + count is
          enough — the bar visualisation carries the magnitude. */}
      {rows.length > 0 ? (
        <ul className="mt-5 space-y-2">
          {rows.map((r) => {
            const meta = CATEGORY_LABELS[r.category];
            const rowPct = shieldStats.totalBlocked > 0 ? (r.count / shieldStats.totalBlocked) * 100 : 0;
            return (
              <li key={r.category} className="rounded-lg border border-[var(--color-border)] p-3">
                <div className="flex items-baseline gap-3">
                  <span className="text-lg leading-none">{meta?.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium">{meta?.label || r.category}</span>
                      <span className="font-mono text-sm tabular-nums">
                        {r.count.toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--color-muted)]">
                      <div
                        className="h-full bg-[var(--color-primary)]"
                        style={{ width: `${rowPct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-5 text-sm text-[var(--color-muted-foreground)]">
          No blocked traffic yet on this link. As real visitors come through, anything that
          looks like a scraper, bot, or filtered region will show up here.
        </p>
      )}

      <p className="mt-6 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-muted-foreground)]">
        Poof Shield uses proprietary detection signals that we
        intentionally don&apos;t spell out &mdash; that&apos;s how we stay one
        step ahead of Meta&apos;s scrapers.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Behavior panel — "what happens when someone visits this link"
// ---------------------------------------------------------------------------

interface BehaviorPanelProps {
  action: 'redirect' | 'page';
  setAction: (v: 'redirect' | 'page') => void;
  escapeIab: boolean;
  setEscapeIab: (v: boolean) => void;
  pageId: string;
  setPageId: (v: string) => void;
  pagesList: PageSummary[];
  currentPageId: string | null;
  /** Destination URL — surfaced as a required field inside the
   *  "Send straight to destination" radio option. Hidden when the
   *  customer picks a landing page (the page IS the destination). */
  targetUrl: string;
  setTargetUrl: (v: string) => void;
  /** Age-gate ("Are you 18+?") interstitial. Applies to BOTH actions
   *  (redirect AND lander) — when on, the gate is shown first, then
   *  the visitor proceeds to whichever action is configured. */
  ageGateEnabled: boolean;
  setAgeGateEnabled: (v: boolean) => void;
  /** Memory: how long a confirmation persists.
   *    'always'  — always ask, never cookie
   *    'session' — until the browser is closed
   *    '24h'     — 24 hours (default) */
  ageGateMemory: 'always' | 'session' | '24h';
  setAgeGateMemory: (v: 'always' | 'session' | '24h') => void;
  /** Also gate the Insta-Shield decoy. */
  ageGateOnDecoy: boolean;
  setAgeGateOnDecoy: (v: boolean) => void;
}

function BehaviorPanel({
  action,
  setAction,
  escapeIab,
  setEscapeIab,
  pageId,
  setPageId,
  pagesList,
  currentPageId,
  targetUrl,
  setTargetUrl,
  ageGateEnabled,
  setAgeGateEnabled,
  ageGateMemory,
  setAgeGateMemory,
  ageGateOnDecoy,
  setAgeGateOnDecoy,
}: BehaviorPanelProps) {
  return (
    // No outer card — surrounding CollapsibleCard provides title + frame.
    <div>

      <div className="mt-2 space-y-2">
        <label className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-[var(--color-muted)]/40">
          <input
            type="radio"
            name="action"
            checked={action === 'redirect'}
            onChange={() => setAction('redirect')}
            className="mt-1"
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Send them straight to the destination URL</div>
            <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
              Classic short link. Recommended when you want one single offer or product
              page with no extra interstitial.
            </div>
            {action === 'redirect' && (
              <div className="mt-3">
                <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                  Destination URL
                </label>
                <input
                  type="text"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  required
                  placeholder="https://example.com/your-offer"
                  className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm outline-none transition focus:border-[var(--color-ring)]"
                />
              </div>
            )}
          </div>
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-[var(--color-muted)]/40">
          <input
            type="radio"
            name="action"
            checked={action === 'page'}
            onChange={() => setAction('page')}
            className="mt-1"
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Show one of your landing pages</div>
            <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
              Good when you want a designed page with multiple links, a bio, social icons —
              not just a single redirect.
            </div>
            {action === 'page' && (
              <div className="mt-3">
                <select
                  value={pageId}
                  onChange={(e) => setPageId(e.target.value)}
                  className="w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm outline-none transition focus:border-[var(--color-ring)]"
                >
                  <option value="">— Pick a page —</option>
                  {pagesList
                    .filter((p) => p.status === 'published' || p.id === currentPageId)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.status === 'draft' ? '(draft)' : ''}
                      </option>
                    ))}
                </select>
                {pagesList.filter((p) => p.status === 'published').length === 0 && (
                  <div className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                    No published pages yet.{' '}
                    <RouterLink to="/app/pages/new" className="text-[var(--color-primary)] hover:underline">
                      Create one
                    </RouterLink>
                    , then come back to attach it here.
                  </div>
                )}
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Escape-IAB sub-toggle — applies to BOTH actions. */}
      <div className="mt-4 border-t border-[var(--color-border)] pt-4">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={escapeIab}
            onChange={(e) => setEscapeIab(e.target.checked)}
            className="mt-1 h-4 w-4"
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">
              Escape the in-app browser first
              <span className="ml-2 rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--color-primary)]">
                recommended
              </span>
            </div>
            <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
              When someone taps from inside <strong>Instagram</strong>,{' '}
              <strong>Threads</strong>, <strong>Facebook</strong>, <strong>Messenger</strong>{' '}
              or an <strong>Android</strong> in-app browser, we'll pop the system browser
              (Safari / Chrome) before {action === 'page' ? 'showing your landing page' : 'redirecting'}.
              This makes affiliate cookies, custom URL schemes, and real share menus work.
              Turn it off if you specifically want the page to render inside the social app's
              webview.
            </div>
          </div>
        </label>
      </div>

      {/* Age-gate sub-toggle — applies to BOTH actions (redirect AND
          lander). Hosted independent of Poof Shield: Shield filters
          bots, the age gate filters age. They can be on together.
          When enabled, two sub-options reveal: retention mode (how
          long the confirmation cookie sticks) and a checkbox to also
          gate the Insta-Shield decoy. */}
      <div className="mt-3 border-t border-[var(--color-border)] pt-4">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={ageGateEnabled}
            onChange={(e) => setAgeGateEnabled(e.target.checked)}
            className="mt-1 h-4 w-4"
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">
              Require 18+ confirmation first
              <span className="ml-2 rounded-full bg-[#ffb688]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#ffb688]">
                NSFW
              </span>
            </div>
            <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
              Before {action === 'page' ? 'your landing page' : 'the destination URL'} loads,
              the visitor sees a clean &ldquo;Are you 18 or older?&rdquo; prompt. One tap to
              confirm. The confirmation is scoped to this link only — so it never carries
              over to other creators&apos; links on your shared domain.
            </div>
          </div>
        </label>

        {ageGateEnabled && (
          <div className="mt-4 space-y-4 rounded-lg border border-[#ffb688]/20 bg-[#ffb688]/5 p-4">
            {/* Memory mode — radio cards. */}
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#ffb688]">
                Once a visitor confirms
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <AgeGateMemoryOption
                  active={ageGateMemory === '24h'}
                  onSelect={() => setAgeGateMemory('24h')}
                  title="Remember 24 hours"
                  helper="Friction-light. Confirmation persists across visits today."
                  recommended
                />
                <AgeGateMemoryOption
                  active={ageGateMemory === 'session'}
                  onSelect={() => setAgeGateMemory('session')}
                  title="Just this session"
                  helper="Cookie clears when the browser closes. Re-asks tomorrow."
                />
                <AgeGateMemoryOption
                  active={ageGateMemory === 'always'}
                  onSelect={() => setAgeGateMemory('always')}
                  title="Ask every time"
                  helper="No cookie ever. The most legally defensible posture."
                />
              </div>
            </div>

            {/* Also gate the decoy — opt-in checkbox. */}
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--surface-deep)] p-3">
              <input
                type="checkbox"
                checked={ageGateOnDecoy}
                onChange={(e) => setAgeGateOnDecoy(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">
                  Also gate the Poof Shield decoy
                </div>
                <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
                  When Poof Shield catches a visitor (bot or otherwise), show the 18+
                  prompt before the decoy too.
                </div>
              </div>
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Radio-card for the age-gate memory mode. Three options stack into a
 * compact 3-column grid; the active one gets a brand-tinted border +
 * filled radio. Same shape as the variation ActionPicker's options so
 * the editor's visual vocabulary stays consistent.
 */
function AgeGateMemoryOption({
  active,
  onSelect,
  title,
  helper,
  recommended,
}: {
  active: boolean;
  onSelect: () => void;
  title: string;
  helper: string;
  recommended?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition ${
        active
          ? 'border-[#ffb688] bg-[#ffb688]/10'
          : 'border-[var(--color-border)] bg-[var(--surface-deep)] hover:border-[#ffb688]/40'
      }`}
    >
      <div className="flex w-full items-start gap-2">
        <span
          aria-hidden
          className={`mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
            active ? 'border-[#ffb688] bg-[#ffb688]' : 'border-[var(--color-border)]'
          }`}
        >
          {active && (
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--surface-deep)]" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium">{title}</span>
            {recommended && (
              <span className="rounded-full bg-[#ffb688]/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#ffb688]">
                recommended
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[11px] leading-snug text-[var(--color-muted-foreground)]">
            {helper}
          </div>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Poof Shield panel — bot / Meta / geo filtering with customer-configured decoy
// ---------------------------------------------------------------------------

const DEFAULT_SHIELD_UI: ShieldConfig = {
  bots: { generic: true, previewers: true, metaCrawlers: true, metaAsn: true },
  countries: { mode: 'off', list: [] },
  // Default decoy type is 'page' — the customer picks one of their own
  // landers as the decoy. Until they pick one (pageId stays empty)
  // serveDecoy in the bouncer falls through to a clean 404, no Poof
  // signal leaked.
  decoy: { type: 'page' },
};

interface ShieldPanelProps {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  config: ShieldConfig;
  setConfig: (v: ShieldConfig) => void;
  pagesList: PageSummary[];
}

function ShieldPanel({ enabled, setEnabled, config, setConfig, pagesList }: ShieldPanelProps) {
  const update = (patch: Partial<ShieldConfig>) => setConfig({ ...config, ...patch });
  const updateBots = (patch: Partial<ShieldConfig['bots']>) =>
    update({ bots: { ...config.bots, ...patch } });
  const updateCountries = (patch: Partial<ShieldConfig['countries']>) =>
    update({ countries: { ...config.countries, ...patch } });
  const updateDecoy = (patch: Partial<ShieldConfig['decoy']>) =>
    update({ decoy: { ...config.decoy, ...patch } });

  return (
    // No outer card chrome — the surrounding CollapsibleCard provides
    // the visual frame, title, and Pro badge. We render the inner
    // controls flush so we don't get a nested-card look.
    <div className="space-y-3">
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Protects your link from being tracked by Meta&apos;s and Bytedance&apos;s scrapers,
        Reviewers, Bots, or unwanted regions. Anyone Poof Shield catches sees a decoy
        you configure below — never your actual destination or landing page.
      </p>

      <label className="mt-3 flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-[var(--color-muted)]/40">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4"
        />
        <span className="text-sm font-medium">
          {enabled ? 'Poof Shield is ON' : 'Enable Poof Shield for this link'}
        </span>
      </label>

      {enabled && (
        <div className="mt-4 space-y-5 border-t border-[var(--color-border)] pt-4">
          {/* What Poof Shield blocks — single status line. We don't
              expose the individual signal toggles in the dashboard;
              when the shield is on, we apply our full set of filters
              (UA fingerprinting, ASN reputation, network heuristics).
              Detection methodology stays internal. */}
          <div className="flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-3">
            <Shield size={18} className="text-[var(--color-primary)]" />
            <div className="text-xs text-[var(--color-muted-foreground)]">
              <div className="font-medium text-[var(--color-foreground)]">
                Full protection is active
              </div>
              <p className="mt-1">
                Scrapers, link previewers, automated tools, and flagged
                traffic are all filtered automatically. You don&apos;t
                need to configure anything — we keep the detection
                signals up to date for you.
              </p>
            </div>
          </div>

          {/* Country filter */}
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              Country filter
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={config.countries.mode === 'off'}
                  onChange={() => updateCountries({ mode: 'off' })}
                />
                Off
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={config.countries.mode === 'allow'}
                  onChange={() => updateCountries({ mode: 'allow' })}
                />
                Allow only
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={config.countries.mode === 'block'}
                  onChange={() => updateCountries({ mode: 'block' })}
                />
                Block these
              </label>
            </div>
            {config.countries.mode !== 'off' && (
              <div className="mt-2">
                {/* Same searchable picker the variations editor uses —
                    type "Israel" or "United…" and match by country name
                    instead of forcing customers to remember ISO codes. */}
                <CountryPicker
                  selected={config.countries.list}
                  onChange={(next) => updateCountries({ list: next })}
                  placeholder="Search countries…"
                />
                {config.countries.mode === 'allow' && (
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Visitors whose country we can't detect are still allowed through (we fail open).
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Decoy — what filtered visitors see. Two options:
              1. `page` (recommended): render one of the customer's own
                 landers. They already have these built; reusing one as
                 the decoy means the decoyed visit is indistinguishable
                 from a normal page-action visit at the response level.
              2. `redirect`: 302 to a customer URL. Same shape as the
                 link's primary "send to destination URL" action.
              The legacy `safe_page` (built-in auto-persona) was removed
              — its config UI was awkward and the customer's own lander
              is strictly better as a decoy. */}
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              What filtered visitors see
            </div>
            <div className="mt-2 space-y-2 text-sm">
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3 hover:bg-[var(--color-primary)]/10">
                <input
                  type="radio"
                  checked={config.decoy.type === 'page'}
                  onChange={() => updateDecoy({ type: 'page' })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Safe Decoy Page</span>
                    <span className="rounded-full bg-[var(--color-primary)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[var(--color-primary-foreground)]">
                      Recommended
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Render one of your own landing pages as the decoy — perfect for
                    cloaking a different offer to filtered visitors. Looks identical
                    to a normal page visit, no redirect chain.
                  </div>
                  {config.decoy.type === 'page' && (
                    <select
                      value={config.decoy.pageId || ''}
                      onChange={(e) => updateDecoy({ pageId: e.target.value })}
                      className="mt-2 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm outline-none transition focus:border-[var(--color-ring)]"
                    >
                      <option value="">— Pick a decoy page —</option>
                      {pagesList
                        .filter((p) => p.status === 'published')
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-[var(--color-muted)]/40">
                <input
                  type="radio"
                  checked={config.decoy.type === 'redirect'}
                  onChange={() => updateDecoy({ type: 'redirect' })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-medium">Redirect to a URL</div>
                  <div className="text-xs text-[var(--color-muted-foreground)]">
                    Send filtered visitors to a destination URL of your choice — same
                    behavior as the link&apos;s &ldquo;send to destination URL&rdquo; action,
                    but only for the traffic Poof Shield catches.
                  </div>
                  {config.decoy.type === 'redirect' && (
                    <input
                      type="url"
                      value={config.decoy.url || ''}
                      onChange={(e) => updateDecoy({ url: e.target.value })}
                      placeholder="https://example.com"
                      className="mt-2 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm outline-none transition focus:border-[var(--color-ring)]"
                    />
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Visitor Variations.
//
// One conceptual feature, two distinct UIs because the user-mental-models
// are different:
//
//   1. Targeted rules (conditional)
//      "Send specific visitors somewhere else." — country, device, source
//      app, referrer, time-of-day. First matching rule wins; rest fall
//      through. Card UI emphasizes the WHEN/SHOW pairing so customers
//      can scan a list of rules without expanding any.
//
//   2. A/B test (weighted)
//      "Randomly split traffic across versions." — sticky per visitor
//      (same person always sees the same variant). Visual traffic bar
//      shows the splits at a glance; cards have a colored accent that
//      matches the bar segment for visual identity.
//
// They share storage (single `variations` array, picked by the bouncer's
// `pickVariation`) but are categorized purely by the presence of a
// `conditions` field:
//
//   • `conditions === undefined`   → A/B variant
//   • `conditions !== undefined`   → Targeted rule (even when empty {} —
//     a freshly-added but unconfigured rule still belongs to the Rules
//     section, otherwise it'd vanish until conditions are set).
//
// Poof Shield filters bots BEFORE this whole machinery runs — surfaced
// with the small eval-order strip at the top of the section.
// ---------------------------------------------------------------------------

interface VariationsSectionProps {
  variations: Variation[];
  setVariations: (next: Variation[]) => void;
  pagesList: PageSummary[];
  shieldEnabled: boolean;
}

function VariationsSection({
  variations,
  setVariations,
  pagesList,
  shieldEnabled,
}: VariationsSectionProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  function patch(idx: number, p: Partial<Variation>) {
    setVariations(variations.map((v, i) => (i === idx ? { ...v, ...p } : v)));
  }

  function patchAction(idx: number, p: Partial<VariationAction>) {
    const next = { ...variations[idx]!.action, ...p };
    patch(idx, { action: next });
  }

  function patchConditions(idx: number, p: Partial<VariationConditions>) {
    const next: VariationConditions = { ...(variations[idx]!.conditions ?? {}), ...p };
    // Note: do NOT auto-delete conditions whose list is empty. The
    // earlier version did, which silently wiped the mode the user just
    // picked ("Is in" / "Does NOT contain") when they hadn't typed any
    // values yet. Now the condition object stays while the user
    // configures it; the chip summary in RuleCard already hides chips
    // with empty lists (so the card stays clean), and the picker handles
    // empty lists gracefully. Conditions are only removed via the
    // explicit "Clear" button, which passes `undefined` for that field.
    //
    // For multi-select arrays (devices, hostApps), we still strip empty
    // arrays — those don't have a mode the user could've intentionally
    // set, so an empty array genuinely means "no constraint."
    if (next.devices && next.devices.length === 0) delete next.devices;
    if (next.hostApps && next.hostApps.length === 0) delete next.hostApps;
    patch(idx, { conditions: next });
  }

  function remove(idx: number) {
    setVariations(variations.filter((_, i) => i !== idx));
  }

  // Two distinct adders so the new variation lands in the right
  // section. Categorization is by presence/absence of `conditions`.
  function nextPosition(): number {
    return variations.reduce((m, v) => Math.max(m, v.position), 0) + 1;
  }
  function addRule() {
    if (variations.length >= 30) return;
    const id = crypto.randomUUID();
    setVariations([
      ...variations,
      {
        id,
        enabled: true,
        name: '',
        position: nextPosition(),
        conditions: {}, // empty object marks it a Rule for UI purposes
        weight: undefined,
        action: { targetUrl: '' },
      },
    ]);
    setExpandedId(id);
  }
  function addVariant() {
    if (variations.length >= 30) return;
    const id = crypto.randomUUID();
    const variantCount = variations.filter((v) => v.conditions === undefined).length;
    setVariations([
      ...variations,
      {
        id,
        enabled: true,
        name: `Variant ${String.fromCharCode(65 + variantCount)}`,
        position: nextPosition(),
        conditions: undefined,
        weight: 50,
        action: { targetUrl: '' },
      },
    ]);
    setExpandedId(id);
  }

  // Display categorization — see top-of-section comment.
  const rules = variations.filter((v) => v.conditions !== undefined);
  const variants = variations.filter((v) => v.conditions === undefined);
  const atCap = variations.length >= 30;

  return (
    // No outer card — surrounding CollapsibleCard provides title +
    // Pro badge + frame.
    <div className="space-y-4">
      <EvalOrderStrip shieldEnabled={shieldEnabled} />
      {shieldEnabled && (
        <p className="text-[11px] text-[var(--color-muted-foreground)]">
          ℹ Bots Poof Shield catches never reach a rule or variant — they see
          your decoy instead.
        </p>
      )}

      <RulesSection
        rules={rules}
        variations={variations}
        expandedId={expandedId}
        setExpandedId={setExpandedId}
        pagesList={pagesList}
        patch={patch}
        patchAction={patchAction}
        patchConditions={patchConditions}
        remove={remove}
        onAdd={addRule}
        atCap={atCap}
      />

      <AbTestSection
        variants={variants}
        variations={variations}
        expandedId={expandedId}
        setExpandedId={setExpandedId}
        pagesList={pagesList}
        patch={patch}
        patchAction={patchAction}
        remove={remove}
        onAdd={addVariant}
        atCap={atCap}
      />

      <div className="text-right text-[10px] text-[var(--color-muted-foreground)]">
        {variations.length} of 30 total
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Eval-order chip strip — pinned under the section legend so the customer
// can always answer "what runs first?" without guessing.
// ---------------------------------------------------------------------------

function EvalOrderStrip({ shieldEnabled }: { shieldEnabled: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--color-muted-foreground)]">
      <span
        className={
          shieldEnabled
            ? 'rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 font-medium text-[var(--color-primary)]'
            : 'rounded-full bg-[var(--color-muted)] px-2 py-0.5 font-medium'
        }
      >
        1. Poof Shield {shieldEnabled ? '(on)' : '(off)'}
      </span>
      <span>→</span>
      <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 font-medium">
        2. Targeted rules
      </span>
      <span>→</span>
      <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 font-medium">
        3. A/B test
      </span>
      <span>→</span>
      <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 font-medium">
        4. Link default
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared section-level types. Both Rules and A/B sections take the same
// edit callbacks + a slice of variations; only the layout differs.
// ---------------------------------------------------------------------------

interface SectionCommonProps {
  variations: Variation[];
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  pagesList: PageSummary[];
  patch: (idx: number, p: Partial<Variation>) => void;
  patchAction: (idx: number, p: Partial<VariationAction>) => void;
  remove: (idx: number) => void;
  onAdd: () => void;
  atCap: boolean;
}

// ---------------------------------------------------------------------------
// Targeted rules — conditional variations.
// ---------------------------------------------------------------------------

function RulesSection({
  rules,
  variations,
  expandedId,
  setExpandedId,
  pagesList,
  patch,
  patchAction,
  patchConditions,
  remove,
  onAdd,
  atCap,
}: SectionCommonProps & {
  rules: Variation[];
  patchConditions: (idx: number, p: Partial<VariationConditions>) => void;
}) {
  return (
    <section
      className="relative overflow-hidden rounded-xl border p-4"
      style={{
        // Subtle violet hue-wash for the Rules section so customers feel
        // "this is the routing area" without reading the heading. The
        // tint sits at ~5% opacity — present enough to orient, light
        // enough that data on top stays readable.
        background:
          'linear-gradient(135deg, rgba(124, 77, 255, 0.06) 0%, rgba(124, 77, 255, 0.02) 60%, transparent 100%)',
        borderColor: 'rgba(124, 77, 255, 0.25)',
      }}
    >
      {/* Soft corner glow for visual depth. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-8 -top-8 h-32 w-32 rounded-full blur-3xl"
        style={{ background: 'rgba(124, 77, 255, 0.12)' }}
      />
      <header className="relative mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--color-primary)]"
            style={{ background: 'rgba(124, 77, 255, 0.18)' }}
          >
            <Target size={16} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold tracking-tight">
              Targeted rules
            </h3>
            <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
              Send specific visitors somewhere different. First matching rule
              wins.
            </p>
          </div>
        </div>
        {rules.length > 0 && (
          <button
            type="button"
            onClick={onAdd}
            disabled={atCap}
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-primary)] px-3 py-1.5 text-xs font-semibold text-[var(--color-primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
          >
            <Plus size={13} /> Add rule
          </button>
        )}
      </header>

      {rules.length === 0 ? (
        <EmptyCard
          icon={<Target size={20} />}
          title="No rules yet"
          description="Add a rule to send visitors from a specific country, device, or app to a different lander or URL."
          ctaLabel="Add your first rule"
          onCta={onAdd}
          disabled={atCap}
          tone="primary"
        />
      ) : (
        <ul className="space-y-2.5">
          {rules.map((r) => {
            const idx = variations.findIndex((x) => x.id === r.id);
            const isOpen = expandedId === r.id;
            return (
              <RuleCard
                key={r.id}
                rule={r}
                pagesList={pagesList}
                isOpen={isOpen}
                onToggleOpen={() => setExpandedId(isOpen ? null : r.id)}
                onToggleEnabled={() => patch(idx, { enabled: !r.enabled })}
                onRemove={() => remove(idx)}
                onPatch={(p) => patch(idx, p)}
                onPatchAction={(p) => patchAction(idx, p)}
                onPatchConditions={(p) => patchConditions(idx, p)}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

function RuleCard({
  rule,
  pagesList,
  isOpen,
  onToggleOpen,
  onToggleEnabled,
  onRemove,
  onPatch,
  onPatchAction,
  onPatchConditions,
}: {
  rule: Variation;
  pagesList: PageSummary[];
  isOpen: boolean;
  onToggleOpen: () => void;
  onToggleEnabled: () => void;
  onRemove: () => void;
  onPatch: (p: Partial<Variation>) => void;
  onPatchAction: (p: Partial<VariationAction>) => void;
  onPatchConditions: (p: Partial<VariationConditions>) => void;
}) {
  const chips = describeConditionsAsChips(rule.conditions ?? {});
  const actionLabel = describeAction(rule.action, pagesList);
  const isOrphan = chips.length === 0;
  const displayName =
    rule.name || autoNameRule(rule.conditions ?? {}) || 'Unconfigured rule';

  return (
    <li
      className={`overflow-hidden rounded-xl border transition ${
        isOpen
          ? 'border-[var(--color-primary)]/40 shadow-[var(--glow-primary)]'
          : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/30'
      } ${!rule.enabled ? 'opacity-50' : ''}`}
      style={{ background: 'var(--surface-glass)' }}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-start gap-3 p-3.5">
        <input
          type="checkbox"
          checked={rule.enabled}
          onChange={onToggleEnabled}
          className="mt-1 h-4 w-4"
          aria-label={`${rule.enabled ? 'Disable' : 'Enable'} rule`}
        />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{displayName}</div>
          {/* WHEN row — condition chips so the customer reads the trigger
              without expanding. */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
              When
            </span>
            {isOrphan ? (
              <span className="text-xs italic text-[var(--color-destructive)]">
                no conditions set — rule never fires
              </span>
            ) : (
              chips.map((chip, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[11px] text-[var(--color-primary)]"
                >
                  {chip.icon}
                  {chip.text}
                </span>
              ))
            )}
          </div>
          {/* SHOW row — what fires when the rule matches. */}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
              Show
            </span>
            <span className="font-mono text-xs text-[var(--color-foreground)]">
              {actionLabel}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onToggleOpen}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--surface-glass-strong)]"
          >
            <Pencil size={11} />
            {isOpen ? 'Done' : 'Edit'}
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded-md p-1.5 text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
            aria-label="Remove rule"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {isOpen && (
        <RuleEditor
          rule={rule}
          pagesList={pagesList}
          onPatch={onPatch}
          onPatchAction={onPatchAction}
          onPatchConditions={onPatchConditions}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// A/B test — weighted variations. Visual traffic bar + colored variant
// cards. Same visitor always sees the same variant (sticky in the picker).
// ---------------------------------------------------------------------------

const VARIANT_TONES = ['#cdbdff', '#7dffa2', '#ffb688', '#ffb4ab', '#a3e635', '#5eead4'];

function AbTestSection({
  variants,
  variations,
  expandedId,
  setExpandedId,
  pagesList,
  patch,
  patchAction,
  remove,
  onAdd,
  atCap,
}: SectionCommonProps & { variants: Variation[] }) {
  const totalWeight = variants.reduce((s, v) => s + (v.weight ?? 0), 0);

  return (
    <section
      className="relative overflow-hidden rounded-xl border p-4"
      style={{
        // Subtle green hue-wash for A/B — matches the Variant traffic-bar
        // accents. "This area is about splitting traffic" reads from the
        // color alone.
        background:
          'linear-gradient(135deg, rgba(125, 255, 162, 0.06) 0%, rgba(125, 255, 162, 0.02) 60%, transparent 100%)',
        borderColor: 'rgba(125, 255, 162, 0.22)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full blur-3xl"
        style={{ background: 'rgba(125, 255, 162, 0.10)' }}
      />
      <header className="relative mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ background: 'rgba(125, 255, 162, 0.18)', color: '#7dffa2' }}
          >
            <Shuffle size={16} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold tracking-tight">
              A/B test
            </h3>
            <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
              Split remaining traffic across versions. Same visitor always sees
              the same variant.
            </p>
          </div>
        </div>
        {variants.length > 0 && (
          <button
            type="button"
            onClick={onAdd}
            disabled={atCap}
            className="inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition hover:brightness-110 disabled:opacity-50"
            style={{ background: '#7dffa2', color: '#003918' }}
          >
            <Plus size={13} /> Add variant
          </button>
        )}
      </header>

      {variants.length === 0 ? (
        <EmptyCard
          icon={<Shuffle size={20} />}
          title="No A/B test yet"
          description="Spin up a 2+ variant test. Different lander, different copy, different offer — see which converts better."
          ctaLabel="Start an A/B test"
          onCta={() => {
            // Create two variants for a real 50/50 split out of the gate.
            onAdd();
            // Defer the second add so React commits the first.
            setTimeout(onAdd, 0);
          }}
          disabled={atCap}
          tone="secondary"
        />
      ) : (
        <>
          <TrafficBar variants={variants} totalWeight={totalWeight} />
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {variants.map((v, i) => {
              const idx = variations.findIndex((x) => x.id === v.id);
              const isOpen = expandedId === v.id;
              return (
                <VariantCard
                  key={v.id}
                  variant={v}
                  variantIndex={i}
                  totalWeight={totalWeight}
                  pagesList={pagesList}
                  isOpen={isOpen}
                  onToggleOpen={() => setExpandedId(isOpen ? null : v.id)}
                  onToggleEnabled={() => patch(idx, { enabled: !v.enabled })}
                  onRemove={() => remove(idx)}
                  onPatch={(p) => patch(idx, p)}
                  onPatchAction={(p) => patchAction(idx, p)}
                />
              );
            })}
          </ul>
          {variants.length === 1 && (
            <div className="mt-3 rounded-lg border border-dashed border-[#ffb688]/40 bg-[#ffb688]/5 p-3 text-xs text-[#ffb688]">
              One variant alone isn&apos;t testing anything. Add a second to compare.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function TrafficBar({
  variants,
  totalWeight,
}: {
  variants: Variation[];
  totalWeight: number;
}) {
  if (totalWeight <= 0) {
    return (
      <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--surface-deep)] p-3 text-center text-xs text-[var(--color-muted-foreground)]">
        All variants have zero weight — the A/B test won&apos;t fire.
      </div>
    );
  }
  return (
    <div>
      <div className="flex h-9 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--surface-deep)]">
        {variants.map((v, i) => {
          const pct = ((v.weight ?? 0) / totalWeight) * 100;
          if (pct <= 0) return null;
          const tone = VARIANT_TONES[i % VARIANT_TONES.length]!;
          const label = v.name || `Variant ${String.fromCharCode(65 + i)}`;
          return (
            <div
              key={v.id}
              className="flex items-center justify-center px-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[#0a0a0a]"
              style={{ width: `${pct}%`, background: tone, minWidth: 28 }}
              title={`${label}: ${Math.round(pct)}%`}
            >
              {pct >= 10 ? `${label} · ${Math.round(pct)}%` : `${Math.round(pct)}%`}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 text-[10px] text-[var(--color-muted-foreground)]">
        Of all traffic that doesn&apos;t match a rule above.
      </div>
    </div>
  );
}

function VariantCard({
  variant,
  variantIndex,
  totalWeight,
  pagesList,
  isOpen,
  onToggleOpen,
  onToggleEnabled,
  onRemove,
  onPatch,
  onPatchAction,
}: {
  variant: Variation;
  variantIndex: number;
  totalWeight: number;
  pagesList: PageSummary[];
  isOpen: boolean;
  onToggleOpen: () => void;
  onToggleEnabled: () => void;
  onRemove: () => void;
  onPatch: (p: Partial<Variation>) => void;
  onPatchAction: (p: Partial<VariationAction>) => void;
}) {
  const tone = VARIANT_TONES[variantIndex % VARIANT_TONES.length]!;
  const pct =
    totalWeight > 0 ? Math.round(((variant.weight ?? 0) / totalWeight) * 100) : 0;
  const actionLabel = describeAction(variant.action, pagesList);

  return (
    <li
      className={`relative overflow-hidden rounded-xl border transition ${
        isOpen
          ? 'border-[var(--color-primary)]/40 shadow-[var(--glow-primary)]'
          : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/30'
      } ${!variant.enabled ? 'opacity-50' : ''}`}
      style={{ background: 'var(--surface-glass)' }}
    >
      {/* Colored accent stripe matching the traffic-bar segment. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1"
        style={{ background: tone }}
      />
      <div className="flex items-start gap-3 p-3.5 pl-4">
        <input
          type="checkbox"
          checked={variant.enabled}
          onChange={onToggleEnabled}
          className="mt-1 h-4 w-4"
          aria-label={`${variant.enabled ? 'Disable' : 'Enable'} variant`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="truncate text-sm font-semibold">
              {variant.name || `Variant ${String.fromCharCode(65 + variantIndex)}`}
            </div>
            <div
              className="font-mono text-base font-bold tabular-nums"
              style={{ color: tone }}
            >
              {pct}%
            </div>
          </div>
          <div className="mt-1 truncate font-mono text-[11px] text-[var(--color-muted-foreground)]">
            {actionLabel}
          </div>
          <div className="mt-3 flex items-center gap-1.5">
            <button
              type="button"
              onClick={onToggleOpen}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--surface-glass-strong)]"
            >
              <Pencil size={11} />
              {isOpen ? 'Done' : 'Edit'}
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="rounded-md p-1.5 text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
              aria-label="Remove variant"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      </div>
      {isOpen && (
        <VariantEditor
          variant={variant}
          pagesList={pagesList}
          onPatch={onPatch}
          onPatchAction={onPatchAction}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Empty-state card — used by both section empty states.
// ---------------------------------------------------------------------------

function EmptyCard({
  icon,
  title,
  description,
  ctaLabel,
  onCta,
  disabled,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaLabel: string;
  onCta: () => void;
  disabled?: boolean;
  tone: 'primary' | 'secondary';
}) {
  const accent = tone === 'secondary' ? '#7dffa2' : '#cdbdff';
  const accentText = tone === 'secondary' ? '#003918' : '#370096';
  return (
    <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--surface-deep)]/40 p-5 text-center">
      <div
        className="mx-auto flex h-10 w-10 items-center justify-center rounded-full"
        style={{ background: `${accent}22`, color: accent }}
      >
        {icon}
      </div>
      <h4 className="mt-3 font-display text-base font-bold tracking-tight">
        {title}
      </h4>
      <p className="mx-auto mt-1.5 max-w-md text-xs text-[var(--color-muted-foreground)]">
        {description}
      </p>
      <button
        type="button"
        onClick={onCta}
        disabled={disabled}
        className="mt-4 inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-semibold transition hover:brightness-110 disabled:opacity-50"
        style={{ background: accent, color: accentText }}
      >
        <Plus size={13} /> {ctaLabel}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RuleEditor — expand-in-place for a Targeted rule. Name + condition
// accordions (country/device/source-app/referrer) + action picker.
// ---------------------------------------------------------------------------

const CONDITION_DEVICES: Array<NonNullable<VariationConditions['devices']>[number]> = [
  'mobile',
  'tablet',
  'desktop',
];
const CONDITION_HOST_APPS: Array<NonNullable<VariationConditions['hostApps']>[number]> = [
  'instagram',
  'threads',
  'facebook',
  'messenger',
  'tiktok',
  'twitter',
  'linkedin',
  'pinterest',
  'reddit',
  'snapchat',
  'direct',
];

function RuleEditor({
  rule,
  pagesList,
  onPatch,
  onPatchAction,
  onPatchConditions,
}: {
  rule: Variation;
  pagesList: PageSummary[];
  onPatch: (p: Partial<Variation>) => void;
  onPatchAction: (p: Partial<VariationAction>) => void;
  onPatchConditions: (p: Partial<VariationConditions>) => void;
}) {
  const conds = rule.conditions ?? {};
  return (
    <div className="space-y-5 border-t border-[var(--color-border)] bg-[var(--surface-deep)]/30 p-4">
      <Field label="Rule name (optional)">
        <input
          type="text"
          value={rule.name || ''}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="e.g. EU mobile · Weekend offer"
          maxLength={120}
          className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-ring)]"
        />
      </Field>

      <div>
        <SectionLabel>When (all must match)</SectionLabel>
        <div className="mt-2 space-y-2">
          <ConditionAccordion
            icon={<Globe size={13} />}
            title="Country"
            summary={
              conds.countries
                ? `${conds.countries.mode === 'in' ? 'is in' : 'is NOT in'} ${
                    conds.countries.list.length > 0
                      ? `${conds.countries.list.length} ${
                          conds.countries.list.length === 1 ? 'country' : 'countries'
                        }`
                      : '—'
                  }`
                : ''
            }
            onClear={() => onPatchConditions({ countries: undefined })}
            isSet={!!conds.countries}
          >
            {/* Mode radios come FIRST so customers can pick the "Is in
                vs Is NOT in" intent before selecting any countries.
                Previously the radios got silently wiped when the list
                was empty — now the mode is preserved with empty lists
                via the patchConditions fix. */}
            <div className="flex gap-3 text-xs">
              <Radio
                checked={conds.countries?.mode === 'in' || !conds.countries}
                onChange={() =>
                  onPatchConditions({
                    countries: { mode: 'in', list: conds.countries?.list ?? [] },
                  })
                }
                label="Is in"
              />
              <Radio
                checked={conds.countries?.mode === 'not_in'}
                onChange={() =>
                  onPatchConditions({
                    countries: { mode: 'not_in', list: conds.countries?.list ?? [] },
                  })
                }
                label="Is NOT in"
              />
            </div>
            {/* Full searchable picker — type "Israel", "United…", or "IL"
                and match country names instead of forcing ISO codes. */}
            <CountryPicker
              selected={conds.countries?.list ?? []}
              onChange={(next) =>
                onPatchConditions({
                  countries: {
                    mode: conds.countries?.mode ?? 'in',
                    list: next,
                  },
                })
              }
              placeholder="Search countries…"
            />
          </ConditionAccordion>

          <ConditionAccordion
            icon={<Smartphone size={13} />}
            title="Device"
            summary={conds.devices?.length ? conds.devices.join(', ') : ''}
            onClear={() => onPatchConditions({ devices: undefined })}
            isSet={!!conds.devices?.length}
          >
            <div className="flex flex-wrap gap-1.5">
              {CONDITION_DEVICES.map((d) => {
                const active = conds.devices?.includes(d) ?? false;
                return (
                  <PillToggle
                    key={d}
                    label={d}
                    active={active}
                    onToggle={() => {
                      const cur = conds.devices ?? [];
                      const next = active ? cur.filter((x) => x !== d) : [...cur, d];
                      onPatchConditions({
                        devices: next.length > 0 ? next : undefined,
                      });
                    }}
                  />
                );
              })}
            </div>
          </ConditionAccordion>

          <ConditionAccordion
            icon={<Smartphone size={13} />}
            title="Source app"
            summary={conds.hostApps?.length ? conds.hostApps.join(', ') : ''}
            onClear={() => onPatchConditions({ hostApps: undefined })}
            isSet={!!conds.hostApps?.length}
          >
            <div className="flex flex-wrap gap-1.5">
              {CONDITION_HOST_APPS.map((h) => {
                const active = conds.hostApps?.includes(h) ?? false;
                return (
                  <PillToggle
                    key={h}
                    label={h}
                    active={active}
                    onToggle={() => {
                      const cur = conds.hostApps ?? [];
                      const next = active ? cur.filter((x) => x !== h) : [...cur, h];
                      onPatchConditions({
                        hostApps: next.length > 0 ? next : undefined,
                      });
                    }}
                  />
                );
              })}
            </div>
          </ConditionAccordion>

          <ConditionAccordion
            icon={<Link2 size={13} />}
            title="Referrer"
            summary={
              conds.referrers
                ? `${
                    conds.referrers.mode === 'contains' ? 'contains' : 'NOT'
                  } ${conds.referrers.list.join(', ')}`
                : ''
            }
            onClear={() => onPatchConditions({ referrers: undefined })}
            isSet={!!conds.referrers}
          >
            {/* Mode radios first so the customer commits to the intent
                ("Contains" vs "Does NOT contain") independently of
                typing values. Default `checked` on "Contains" when no
                referrer condition has been set yet — gives a sensible
                starting point without forcing them to click twice. */}
            <div className="flex gap-3 text-xs">
              <Radio
                checked={conds.referrers?.mode === 'contains' || !conds.referrers}
                onChange={() =>
                  onPatchConditions({
                    referrers: { mode: 'contains', list: conds.referrers?.list ?? [] },
                  })
                }
                label="Contains"
              />
              <Radio
                checked={conds.referrers?.mode === 'not_contains'}
                onChange={() =>
                  onPatchConditions({
                    referrers: { mode: 'not_contains', list: conds.referrers?.list ?? [] },
                  })
                }
                label="Does NOT contain"
              />
            </div>
            <input
              type="text"
              value={(conds.referrers?.list ?? []).join(', ')}
              onChange={(e) =>
                onPatchConditions({
                  referrers: {
                    mode: conds.referrers?.mode ?? 'contains',
                    list: e.target.value
                      .split(/[\s,]+/)
                      .map((s) => s.trim().toLowerCase())
                      .filter((s) => s.length > 0),
                  },
                })
              }
              placeholder="instagram.com, t.co…"
              className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2 py-1 font-mono text-xs outline-none focus:border-[var(--color-ring)]"
            />
          </ConditionAccordion>
        </div>
      </div>

      <div>
        <SectionLabel>Then show</SectionLabel>
        <ActionPicker
          action={rule.action}
          pagesList={pagesList}
          onChange={onPatchAction}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VariantEditor — expand-in-place for an A/B variant. Name + weight +
// action picker. No conditions (those belong in the Rules section).
// ---------------------------------------------------------------------------

function VariantEditor({
  variant,
  pagesList,
  onPatch,
  onPatchAction,
}: {
  variant: Variation;
  pagesList: PageSummary[];
  onPatch: (p: Partial<Variation>) => void;
  onPatchAction: (p: Partial<VariationAction>) => void;
}) {
  return (
    <div className="space-y-5 border-t border-[var(--color-border)] bg-[var(--surface-deep)]/30 p-4">
      <Field label="Variant name">
        <input
          type="text"
          value={variant.name || ''}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="e.g. Variant A · Hero copy v2"
          maxLength={120}
          className="block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-ring)]"
        />
      </Field>

      <Field label="Weight">
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={variant.weight ?? 0}
            onChange={(e) => onPatch({ weight: Number(e.target.value) })}
            className="flex-1"
          />
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={1000}
              value={variant.weight ?? 0}
              onChange={(e) =>
                onPatch({ weight: Math.max(0, Number(e.target.value) || 0) })
              }
              className="w-16 rounded-md border border-[var(--color-border)] bg-transparent px-2 py-1 text-center font-mono text-xs outline-none focus:border-[var(--color-ring)]"
            />
            <span className="text-xs text-[var(--color-muted-foreground)]">pts</span>
          </div>
        </div>
        <p className="mt-1.5 text-[11px] text-[var(--color-muted-foreground)]">
          Higher weight = more traffic. The percentage in the bar above is
          relative to all variant weights combined.
        </p>
      </Field>

      <div>
        <SectionLabel>Then show</SectionLabel>
        <ActionPicker
          action={variant.action}
          pagesList={pagesList}
          onChange={onPatchAction}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionPicker — shared between rule + variant editors.
// Radio cards: Different URL / Different lander / Inherit.
// ---------------------------------------------------------------------------

function ActionPicker({
  action,
  pagesList,
  onChange,
}: {
  action: VariationAction;
  pagesList: PageSummary[];
  onChange: (p: Partial<VariationAction>) => void;
}) {
  // Track mode in LOCAL state, not derived from action shape — clicking
  // "A different URL" sets targetUrl='' (empty), and a derived mode would
  // immediately fall back to a default, making the radio appear unselected.
  // With local state, picking a mode is a UI commitment that doesn't
  // depend on the user having typed a value yet.
  // Default to 'page' for new variants — most A/B tests are between
  // alternate landers (different copy/offer/social-proof) rather than
  // different URLs. Existing variants with a targetUrl still open as
  // 'redirect' so we don't surprise current configs.
  const [mode, setMode] = useState<'redirect' | 'page'>(() => {
    if (action.targetUrl) return 'redirect';
    return 'page';
  });

  function pickMode(m: 'redirect' | 'page') {
    setMode(m);
    if (m === 'redirect') {
      onChange({ pageId: undefined, targetUrl: action.targetUrl || '' });
    } else {
      const defaultPage = pagesList.find((p) => p.status === 'published')?.id || '';
      onChange({ targetUrl: undefined, pageId: action.pageId || defaultPage });
    }
  }

  return (
    <div className="mt-2 space-y-2">
      <ActionOption
        active={mode === 'page'}
        onSelect={() => pickMode('page')}
        title="A different landing page"
        helper="Pick one of your published landers."
      >
        {mode === 'page' && (
          <select
            value={action.pageId || ''}
            onChange={(e) => onChange({ pageId: e.target.value })}
            className="mt-1.5 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-ring)]"
          >
            <option value="">— Pick a page —</option>
            {pagesList
              .filter((p) => p.status === 'published')
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        )}
      </ActionOption>
      <ActionOption
        active={mode === 'redirect'}
        onSelect={() => pickMode('redirect')}
        title="A different URL"
        helper="Send these visitors to a custom destination URL."
      >
        {mode === 'redirect' && (
          <input
            type="text"
            value={action.targetUrl || ''}
            onChange={(e) => onChange({ targetUrl: e.target.value })}
            placeholder="https://example.com/override"
            className="mt-1.5 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-ring)]"
          />
        )}
      </ActionOption>
    </div>
  );
}

function ActionOption({
  active,
  onSelect,
  title,
  helper,
  children,
}: {
  active: boolean;
  onSelect: () => void;
  title: string;
  helper: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border p-2.5 transition ${
        active
          ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8'
          : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/30 hover:bg-[var(--surface-glass)]'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full items-start gap-2 text-left"
      >
        <span
          aria-hidden
          className={`mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
            active
              ? 'border-[var(--color-primary)] bg-[var(--color-primary)]'
              : 'border-[var(--color-border)]'
          }`}
        >
          {active && (
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--surface-deep)]" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{title}</div>
          <div className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">
            {helper}
          </div>
        </div>
      </button>
      {children && <div className="mt-1.5">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small bits — condition accordion + pill toggle + radio + labels.
// ---------------------------------------------------------------------------

function ConditionAccordion({
  icon,
  title,
  summary,
  isSet,
  onClear,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  summary: string;
  isSet: boolean;
  onClear: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(isSet);
  return (
    <div
      className={`rounded-md border p-2 ${
        isSet
          ? 'border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5'
          : 'border-[var(--color-border)]'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-left text-xs font-medium"
      >
        <span
          className={
            isSet ? 'text-[var(--color-primary)]' : 'text-[var(--color-muted-foreground)]'
          }
        >
          {icon}
        </span>
        <span>{title}</span>
        {summary && (
          <span className="truncate text-[var(--color-muted-foreground)]">
            · {summary}
          </span>
        )}
        <ChevronDown
          size={12}
          className={`ml-auto shrink-0 text-[var(--color-muted-foreground)] transition ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {children}
          {isSet && (
            <button
              type="button"
              onClick={onClear}
              className="text-[10px] text-[var(--color-destructive)] hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PillToggle({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rounded-full px-2.5 py-1 text-[11px] font-medium capitalize transition ${
        active
          ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
          : 'border border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/30 hover:text-[var(--color-foreground)]'
      }`}
    >
      {label}
    </button>
  );
}

function Radio({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5">
      <input type="radio" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
      {children}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Helpers — condition chip extraction, action label, auto-name. Pure
// functions, no React. Used by RuleCard + VariantCard for at-a-glance
// summaries; the editors don't need them.
// ---------------------------------------------------------------------------

function describeConditionsAsChips(
  c: VariationConditions,
): Array<{ icon: React.ReactNode; text: string }> {
  const chips: Array<{ icon: React.ReactNode; text: string }> = [];
  if (c.countries && c.countries.list.length > 0) {
    // Truncate long country lists in the summary chip — the editor
    // shows the full list, the card just needs an at-a-glance summary.
    const list = c.countries.list;
    const summary =
      list.length <= 3
        ? list.join(', ')
        : `${list.slice(0, 2).join(', ')} +${list.length - 2} more`;
    chips.push({
      icon: <Globe size={11} />,
      text: `${c.countries.mode === 'in' ? '' : 'not '}${summary}`,
    });
  }
  if (c.devices && c.devices.length > 0) {
    chips.push({ icon: <Smartphone size={11} />, text: c.devices.join(', ') });
  }
  if (c.hostApps && c.hostApps.length > 0) {
    chips.push({
      icon: <Smartphone size={11} />,
      text: `from ${c.hostApps.join(', ')}`,
    });
  }
  if (c.referrers && c.referrers.list.length > 0) {
    chips.push({
      icon: <Link2 size={11} />,
      text: `${c.referrers.mode === 'contains' ? '' : 'not '}${c.referrers.list.join(', ')}`,
    });
  }
  if (c.hours) {
    chips.push({
      icon: <Clock size={11} />,
      text: `${c.hours.start}–${c.hours.end} ${c.hours.tz}`,
    });
  }
  return chips;
}

function describeAction(a: VariationAction, pagesList: PageSummary[]): string {
  if (a.pageId) {
    const p = pagesList.find((x) => x.id === a.pageId);
    return `lander · ${p?.name || 'Landing page'}`;
  }
  if (a.targetUrl) return `redirect · ${a.targetUrl}`;
  return 'inherit link default';
}

function autoNameRule(c: VariationConditions): string {
  const chips = describeConditionsAsChips(c);
  if (chips.length === 0) return '';
  return chips.map((x) => x.text).join(' + ');
}

function BotToggle({
  checked,
  onChange,
  title,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  hint: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-[var(--color-muted)]/40">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4"
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">{hint}</div>
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl glass p-5">
      <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </div>
      <div className="mt-2 truncate text-3xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{hint}</div>}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium transition hover:bg-[var(--color-muted)]"
    >
      {copied ? '✓ Copied' : 'Copy URL'}
    </button>
  );
}

function prettyHostApp(s: string): string {
  switch (s) {
    case 'instagram':
      return 'Instagram';
    case 'threads':
      return 'Threads';
    case 'facebook':
      return 'Facebook';
    case 'tiktok':
      return 'TikTok';
    case 'twitter':
      return 'X (Twitter)';
    case 'linkedin':
      return 'LinkedIn';
    case 'pinterest':
      return 'Pinterest';
    case 'reddit':
      return 'Reddit';
    case 'snapchat':
      return 'Snapchat';
    case 'android-webview':
      return 'Android';
    case 'unknown':
    case '—':
      return s;
    default:
      return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

function errorMessageFor(code: string): string {
  switch (code) {
    case 'target_url_required':
      return 'A destination URL is required.';
    case 'target_url_invalid':
      return "That URL doesn't look right.";
    case 'short_code_taken':
      return 'That short code is already taken on the chosen domain.';
    case 'domain_not_yours':
      return "That domain doesn't belong to your workspace.";
    case 'domain_not_ready':
      return "That domain isn't fully provisioned yet (SSL or DNS still pending).";
    case 'page_not_yours':
      return "That page doesn't belong to your workspace.";
    case 'page_not_published':
      return 'Publish the page first, then attach it here.';
    case 'page_required_for_page_action':
      return 'Pick a page to show.';
    case 'default_action_invalid':
      return "That's not a valid action.";
    case 'not_found':
      return "This link doesn't exist (or you don't have access).";
    default:
      return code;
  }
}

// ---------------------------------------------------------------------------
// CollapsibleCard — settings-section row chip that expands inline.
//
// Why: the link-detail page used to render every settings panel
// expanded on mount, producing ~3 screens of scrolling between the
// analytics block and the delete button. Customers reported it as
// "chaotic." Replaced with these collapsibles so the page reads as
// analytics-first + dense one-line summaries of every setting.
//
// Open/closed state persists in localStorage keyed by section id so a
// customer who wants Shield always open gets that without re-clicking
// every page load. First-load default is closed (controlled by
// `defaultOpen`) so the page lands calm.
// ---------------------------------------------------------------------------
function CollapsibleCard({
  id,
  title,
  badge,
  summary,
  defaultOpen = false,
  children,
}: {
  id: string;
  title: string;
  /** Optional pill rendered next to the title (e.g. "Pro · free during beta"). */
  badge?: React.ReactNode;
  /** One-line state-of-this-section summary shown when collapsed. */
  summary: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const storageKey = `linkdetail.collapse.${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch {
      // Private mode / storage disabled — fall through to default.
    }
    return defaultOpen;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? '1' : '0');
    } catch {
      // Best-effort.
    }
  }, [open, storageKey]);

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--surface-glass)]/40">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-[var(--surface-glass)]/60"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{title}</span>
            {badge}
          </div>
          {!open && summary != null && (
            <div className="mt-1 truncate text-sm text-[var(--color-muted-foreground)]">
              {summary}
            </div>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`flex-shrink-0 text-[var(--color-muted-foreground)] transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        // Body baseline is 15px with arbitrary-variant bumps for the
        // common too-small Tailwind utilities used by the inner panels
        // (text-xs / text-[10px] / text-[11px] / text-[11.5px] /
        // text-[12.5px]). Centralising the size bump here means the
        // analytics block above stays untouched at its current sizes —
        // only settings content gets more readable. !important is used
        // because text-xs (font-size: 0.75rem) has the same specificity
        // as our override class otherwise.
        <div
          className={[
            'border-t border-[var(--color-border)] px-5 py-4 text-[15px]',
            '[&_.text-xs]:!text-[13.5px]',
            '[&_.text-\\[10px\\]]:!text-[11.5px]',
            '[&_.text-\\[11px\\]]:!text-[12.5px]',
            '[&_.text-\\[11.5px\\]]:!text-[13px]',
            '[&_.text-\\[12.5px\\]]:!text-[13.5px]',
            '[&_.text-\\[13px\\]]:!text-[14px]',
          ].join(' ')}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary builders — render the one-liners shown when each collapsible
// card is closed. Pure functions of the current form state.
// ---------------------------------------------------------------------------

function summarizeDestination(args: {
  action: 'redirect' | 'page';
  targetUrl: string;
  pageId: string;
  pagesList: PageSummary[];
  escapeIab: boolean;
  ageGateEnabled: boolean;
}): string {
  const { action, targetUrl, pageId, pagesList, escapeIab, ageGateEnabled } = args;
  let what: string;
  if (action === 'page') {
    const page = pagesList.find((p) => p.id === pageId);
    what = page ? `Lander: ${page.name}` : 'Lander: (none picked)';
  } else {
    const clean = (targetUrl || '').replace(/^https?:\/\//i, '');
    what = clean ? `→ ${clean.length > 40 ? clean.slice(0, 40) + '…' : clean}` : '(no URL set)';
  }
  const flags: string[] = [];
  flags.push(escapeIab ? 'in-app escape ON' : 'in-app escape OFF');
  if (ageGateEnabled) flags.push('18+ gate');
  return `${what} · ${flags.join(' · ')}`;
}

function summarizeShield(args: {
  shieldEnabled: boolean;
  shield: ShieldConfig;
  pagesList: PageSummary[];
}): string {
  const { shieldEnabled, shield, pagesList } = args;
  if (!shieldEnabled) return 'Off — no filtering';
  let decoy: string;
  if (shield.decoy.type === 'page') {
    const page = pagesList.find((p) => p.id === shield.decoy.pageId);
    decoy = page ? `decoy: ${page.name}` : 'decoy: (none picked)';
  } else if (shield.decoy.type === 'redirect') {
    decoy = shield.decoy.url ? `decoy → ${shield.decoy.url.replace(/^https?:\/\//i, '')}` : 'decoy: (no URL)';
  } else {
    decoy = 'decoy: 404';
  }
  const geo =
    shield.countries.mode === 'off'
      ? 'geo: off'
      : `geo: ${shield.countries.mode} (${shield.countries.list.length})`;
  return `ON · ${decoy} · ${geo}`;
}

function summarizeVariations(variations: Variation[]): string {
  if (!variations.length) return 'None — every visitor sees the default action';
  const rules = variations.filter((v) => v.conditions !== undefined).length;
  const abs = variations.filter((v) => v.conditions === undefined).length;
  const parts: string[] = [];
  if (rules) parts.push(`${rules} targeted rule${rules > 1 ? 's' : ''}`);
  if (abs) parts.push(`${abs} A/B variant${abs > 1 ? 's' : ''}`);
  return parts.join(' · ');
}
